import { stateful, useEffect, useMemo, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { getCallSuccessReviews, getCallSuccessStats, setCallSuccessReview } from '../../db/call-success-resolvers.js';
import { CallSuccessStore, type SuccessPeriod, type SuccessStatus } from './call-success-store.js';

export const createCallSuccessStore = (includeStats = true) =>
  new CallSuccessStore(
    {
      reviews: ids => getCallSuccessReviews(ids)(),
      save: (id, status) => setCallSuccessReview(id, status)(),
      stats: (search, period) => getCallSuccessStats(search, period)(),
    },
    { includeStats },
  );
const colors = {
  yes: { background: '#ecfdf3', color: '#166534', border: '#86cfa4' },
  no: { background: '#fff1f2', color: '#a71930', border: '#f0a0ad' },
  ignored: { background: '#f1f3f6', color: '#475467', border: '#cbd2dc' },
  '': { background: '#fff', color: '#667085', border: '#cbd2dc' },
};
export const CallSuccessSelect = stateful(({ callID, store }: { callID: string; store: CallSuccessStore }) => {
  const [revision] = useState({ value: 0 });
  const [, rerender] = useState(0);
  useEffect(() => store.subscribeRow(callID, () => rerender(++revision.value)), [store, callID]);
  const row = store.row(callID);
  const color = colors[row.value];
  const change = (event: Event) => {
    const select = event.target as HTMLSelectElement;
    const requested = select.value as SuccessStatus;
    // A native select changes before the request: put the persisted value back until save succeeds.
    select.value = store.row(callID).value;
    void store.save(callID, requested);
  };
  return html`<div style="min-width:164px;max-width:240px">
    <select
      aria-label=${`Success für Anruf ${callID}`}
      .value=${row.value}
      ?disabled=${!callID || !row.loaded || row.saving}
      aria-busy=${row.saving || !row.loaded ? 'true' : 'false'}
      style=${`width:100%;min-height:36px;border-radius:7px;padding:6px 9px;font-family:inherit;font-size:13px;font-weight:600;line-height:1.4;cursor:pointer;background:${color.background};color:${color.color};border:1px solid ${color.border}`}
      @change=${change}
    >
      <option value="">—</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
      <option value="ignored">Doesn't matter</option>
    </select>
    <div style="font-size:11px;line-height:1.4;margin-top:4px" aria-live="polite">
      ${row.error
        ? html`<span role="alert" style="color:#a71930">${row.error}</span>`
        : html`<span role="status" style="color:#667085"
            >${row.saving ? 'Speichert…' : !row.loaded ? 'Lädt…' : row.saved ? 'Gespeichert' : ''}</span
          >`}
    </div>
  </div>`;
});

/** The same manual review as the overview; this card never changes the automatic call outcome. */
export const CallSuccessDetail = stateful(({ callID }: { callID: string }) => {
  const store = useMemo(() => [createCallSuccessStore(false)], [callID]);
  useEffect(() => {
    const stop = store.start();
    return () => {
      stop();
      store.dispose();
    };
  }, [store]);
  return html`
    <style>
      .zw-success-detail {
        padding: 24px;
        border: 1px solid #dce5ef;
        border-radius: 14px;
        background: linear-gradient(120deg, #f7fbff 0%, #fff 76%);
        color: #344054;
      }
      .zw-success-detail-layout {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        gap: 18px 24px;
      }
      .zw-success-detail-copy {
        flex: 1 1 220px;
      }
      .zw-success-detail-eyebrow {
        margin: 0 0 7px;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #667085;
      }
      .zw-success-detail h3 {
        margin: 0;
        font-size: 18px;
        line-height: 1.4;
        font-weight: 700;
        color: #1d2939;
      }
      .zw-success-detail-description {
        margin: 7px 0 0;
        font-size: 13px;
        line-height: 1.6;
        color: #667085;
      }
      .zw-success-detail-choice {
        flex: 0 1 200px;
        min-width: 164px;
      }
      .zw-success-detail-note {
        margin: 18px 0 0;
        padding-top: 13px;
        border-top: 1px solid #e6ebf1;
        color: #667085;
        font-size: 12px;
        line-height: 1.6;
      }
      @media (max-width: 620px) {
        .zw-success-detail {
          padding: 18px;
        }
        .zw-success-detail-choice {
          flex-basis: 100%;
        }
      }
    </style>
    <section class="zw-success-detail" aria-label="Success für diesen Anruf">
      <div class="zw-success-detail-layout">
        <div class="zw-success-detail-copy">
          <p class="zw-success-detail-eyebrow">Ihre Bewertung</p>
          <h3>Success</h3>
          <p class="zw-success-detail-description">War dieser Anruf erfolgreich?</p>
        </div>
        <div class="zw-success-detail-choice">${CallSuccessSelect({ callID, store })}</div>
      </div>
      <p class="zw-success-detail-note">
        Ihre Auswahl wird auch in der Anrufübersicht angezeigt. Nur Yes und No zählen zur Success rate. Doesn't matter
        und nicht bewertete Anrufe bleiben unberücksichtigt.
      </p>
    </section>
  `;
});

export const CallSuccessSummary = stateful(
  ({
    store,
    search,
    period,
    refresh,
  }: {
    store: CallSuccessStore;
    search: string;
    period: SuccessPeriod;
    refresh: number;
  }) => {
    const [revision] = useState({ value: 0 });
    const [, rerender] = useState(0);
    useEffect(() => {
      const unsubscribe = store.subscribe(() => rerender(++revision.value));
      const stop = store.start();
      return () => {
        unsubscribe();
        stop();
        store.dispose();
      };
    }, [store]);
    useEffect(() => {
      store.configure(search, period);
    }, [store, search, period, refresh]);
    const stats = store.matchesScope(search, period) ? store.stats : null;
    const reviewed = stats ? stats.yes + stats.no : 0;
    const rate =
      stats && reviewed
        ? new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format((stats.yes / reviewed) * 100) + ' %'
        : '—';
    const counts = [
      { label: 'Yes', count: stats?.yes, color: '#166534', background: '#ecfdf3' },
      { label: 'No', count: stats?.no, color: '#a71930', background: '#fff1f2' },
      { label: "Doesn't matter", count: stats?.ignored, color: '#475467', background: '#f1f3f6' },
      { label: 'Unreviewed', count: stats?.unreviewed, color: '#667085', background: '#f8fafc' },
    ];
    return html` <style>
        .zw-success-summary {
          box-sizing: border-box;
          margin: 0 0 22px;
          padding: 24px;
          border: 1px solid #e1e7ee;
          border-radius: 14px;
          background: linear-gradient(120deg, #f7fbff 0%, #fff 62%);
          color: #202630;
        }
        .zw-success-layout {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          align-items: center;
        }
        .zw-success-head {
          flex: 1 1 190px;
        }
        .zw-success-head h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
        }
        .zw-success-rate {
          margin: 5px 0 3px;
          font-size: 42px;
          line-height: 1.15;
          letter-spacing: -0.04em;
          font-weight: 750;
          color: #152d4a;
        }
        .zw-success-counts {
          display: grid;
          grid-template-columns: repeat(4, minmax(100px, 1fr));
          gap: 10px;
          flex: 3 1 480px;
        }
        .zw-success-count {
          padding: 14px;
          border-radius: 9px;
        }
        .zw-success-count dt {
          font-size: 12px;
          font-weight: 600;
          margin: 0 0 5px;
        }
        .zw-success-count dd {
          font-size: 25px;
          line-height: 1.3;
          font-weight: 700;
          margin: 0;
        }
        .zw-success-note {
          margin: 5px 0 0;
          font-size: 12px;
          line-height: 1.55;
          color: #667085;
        }
        .zw-success-footer {
          margin: 18px 0 0;
          padding-top: 13px;
          border-top: 1px solid #e6ebf1;
          font-size: 12px;
          color: #667085;
        }
        @media (max-width: 620px) {
          .zw-success-summary {
            padding: 18px;
          }
          .zw-success-counts {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            flex-basis: 100%;
          }
        }
      </style>
      <section
        class="zw-success-summary"
        aria-label="Manuelle Success-Auswertung"
        aria-busy=${store.statsLoading ? 'true' : 'false'}
      >
        <div class="zw-success-layout">
          <div class="zw-success-head">
            <h3>Success rate</h3>
            <p class="zw-success-rate">${rate}</p>
            <p class="zw-success-note">Nur manuell bewertet · Yes / (Yes + No)</p>
            <p class="zw-success-note">
              ${stats
                ? `${reviewed} gewertete Bewertungen`
                : store.statsLoading
                  ? 'Bewertungen werden geladen…'
                  : 'Keine Auswertung verfügbar.'}
            </p>
          </div>
          <dl class="zw-success-counts">
            ${counts.map(
              item =>
                html`<div class="zw-success-count" style=${`color:${item.color};background:${item.background}`}>
                  <dt>${item.label}</dt>
                  <dd>${item.count ?? '—'}</dd>
                </div>`,
            )}
          </dl>
        </div>
        <p class="zw-success-footer">
          ${stats
            ? `${stats.total} passende Anrufe · Auswertung über alle Treffer des gewählten Zeitraums und der Suche.`
            : 'Die Auswertung gilt für den gewählten Zeitraum und die Suche.'}
          ${stats && stats.total > 500
            ? html`<br />Die Liste zeigt die neuesten 500 Treffer; die Auswertung berücksichtigt alle ${stats.total}.`
            : nothing}
          <br />Doesn't matter und Unreviewed zählen nicht zur Success rate. Automatische Ergebnisse ändern keine
          Bewertung.
        </p>
        ${store.statsError
          ? html`<p role="alert" style="color:#a71930;font-size:12px;margin:10px 0 0">
              ${store.statsError}
              ${stats ? 'Die angezeigten Zahlen stammen aus der letzten erfolgreichen Abfrage.' : ''}
            </p>`
          : nothing}
        ${store.statsLoading && stats ? html`<p role="status" class="zw-success-note">Aktualisiert…</p>` : nothing}
      </section>`;
  },
);
